import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Document, Page, pdfjs } from 'react-pdf'
import { useParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { EAnnotationEngine, PDFAnchorFragment } from '@/features/annotation'
import {
  asPdfAnnotation,
  asPdfPageProxyLike,
} from '@/features/annotation-management'
import { useAuthStore } from '@/features/auth/authStore'
import { useDocumentStore } from '@/features/document'
import { ViewerSurface } from '@/features/view-tfl-uploaded/components'
import { AddCommentDialog } from '@/features/view-tfl-uploaded/components/AddCommentDialog'
import { CommentAvatar } from '@/features/view-tfl-uploaded/components/CommentAvatar/CommentAvatar'
import { SelectionBubble } from '@/features/view-tfl-uploaded/components/SelectionBubble'
import { usePdfViewerController } from '@/features/view-tfl-uploaded/hooks/usePdfViewerController'
import { TflMeta } from '@/features/view-tfl-uploaded/types'
import { cn } from '@/utils'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

type PDFViewerProps = {
  documentId: string
  isLoading: boolean
  error: Error | null
  refetch: () => void
  meta: Partial<TflMeta> | undefined
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  documentId,
  isLoading,
  error,
  refetch,
  meta,
}) => {
  const { id } = useParams<{ id: string }>()
  const getListDLViewerHasAccess = useAuthStore(
    (state) => state.getListDLViewerHasAccess
  )
  const listDLViewerHasAccess = getListDLViewerHasAccess() as string[]
  const isViewer = listDLViewerHasAccess.includes(id as string)

  const [totalPages, setTotalPages] = useState<number | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pdfError, setPdfError] = useState<string | null>(null)
  const [signedUrl, setSignedUrl] = useState<string | undefined>(
    meta?.signedUrl
  )
  const pdfContainerRef = React.useRef<HTMLDivElement>(null)
  const [currentVersionId, setCurrentVersionId] = useState<string | undefined>(
    meta?.version_id
  )

  useEffect(() => {
    const newVersionId = meta?.version_id
    const newSignedUrl = meta?.signedUrl
    if (!newVersionId || !newSignedUrl) return

    const isNewDoc = currentVersionId !== newVersionId

    if (isNewDoc) {
      setSignedUrl(newSignedUrl)
      setCurrentVersionId(newVersionId)

      // resets
      setTotalPages(null)
      setNumPages(0)
      setPdfError(null)
    } else if (!signedUrl) {
      // First load: allow setting even if version matches
      setSignedUrl(newSignedUrl)
    }
  }, [meta, signedUrl, currentVersionId])

  const isRendered = useMemo(
    () => !!numPages && !!totalPages && numPages === totalPages,
    [numPages, totalPages]
  )

  // Set up PDF viewer controller for text selection and annotations
  const {
    containerRef,
    pdfEngineRef,
    pageRefsMap,
    handlePageLoadSuccess,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    pendingBubble,
    dialogState,
    dialog,
    avatars,
    showCommentAvatars,
    onAvatarClick,
    onClickOutside,
    onMouseDown,
    bubbleOffset,
    pageRegistryVersion,
  } = usePdfViewerController({
    documentId,
    pdfContainerRef,
    numPages: isRendered ? numPages : 0,
  })

  const focusSearchResult = useDocumentStore((state) => state.focusSearchResult)

  // Draw focus search result highlight on focusHighlightCanvas when sidebar focus matches this document/version
  useEffect(() => {
    const engine = pdfEngineRef.current
    const refsMap = pageRefsMap.current
    if (!engine || !refsMap) return

    const focusCanvasByPage = new Map<number, HTMLCanvasElement | null>()
    refsMap.forEach((refs, pageIndex) => {
      focusCanvasByPage.set(pageIndex, refs.focusHighlightCanvas ?? null)
    })

    const matchesFocus =
      focusSearchResult?.documentId === documentId &&
      focusSearchResult?.versionId === currentVersionId

    if (matchesFocus && focusSearchResult?.keywords?.length) {
      engine.drawFocusHighlight(
        focusSearchResult.keywords,
        focusSearchResult.index,
        focusCanvasByPage
      )
      // Scroll the page containing the focused highlight (matchIndex) into view
      const focusRectsByPage = engine.getFocusHighlightRects(
        focusSearchResult.keywords,
        focusSearchResult.index
      )
      const firstPageIndex = focusRectsByPage[0]?.pageIndex
      if (firstPageIndex !== undefined) {
        const pageRefs = refsMap.get(firstPageIndex)
        pageRefs?.wrapper?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    } else {
      engine.drawFocusHighlight([], 0, focusCanvasByPage)
    }
  }, [
    documentId,
    currentVersionId,
    focusSearchResult,
    pageRegistryVersion,
    pdfEngineRef,
    pageRefsMap,
  ])

  const onDocumentLoadSuccess = ({
    numPages: totalPages,
  }: {
    numPages: number
  }) => {
    setTotalPages(totalPages)
    setNumPages(0)
    setPdfError(null)
  }

  const onDocumentLoadError = (error: Error) => {
    setPdfError(
      error.message ||
        'Failed to load PDF. Please check the file and try again.'
    )
  }

  const setPageRef = (
    pageIndex: number,
    refType:
      | 'wrapper'
      | 'highlightCanvas'
      | 'focusHighlightCanvas'
      | 'controlCanvas'
      | 'bubbleLayer',
    element: HTMLElement | HTMLCanvasElement | null
  ) => {
    if (!pageRefsMap.current) return
    const refs = pageRefsMap.current.get(pageIndex) || {
      wrapper: null,
      highlightCanvas: null,
      focusHighlightCanvas: null,
      controlCanvas: null,
      bubbleLayer: null,
    }
    ;(refs as Record<string, unknown>)[refType] = element
    pageRefsMap.current.set(pageIndex, refs)
  }

  const addCommentBubblePortal = useMemo(() => {
    if (isViewer || !pendingBubble.show || !pdfEngineRef.current) return null

    const metadata = dialog.pendingAnnotation.metadata
    if (metadata?.engine !== EAnnotationEngine.PDF) return null

    const pdf = asPdfAnnotation(metadata)
    const fragment = pdf?.fragments?.[0] as PDFAnchorFragment | undefined
    if (!fragment) return null

    const layer = pdfEngineRef.current.getBubbleLayerEl(fragment.pageIndex)
    const rect = pdfEngineRef.current.getAnchorPageRect(fragment)
    if (!layer || !rect) return null

    return createPortal(
      <SelectionBubble
        position={{
          top: rect.y + (bubbleOffset.top ?? 0),
          left: rect.x + (bubbleOffset.left ?? 0),
        }}
        onAddComment={pendingBubble.onAddComment}
      />,
      layer
    )
  }, [
    pendingBubble.show,
    pendingBubble.onAddComment,
    dialog.pendingAnnotation.metadata,
    pdfEngineRef,
    bubbleOffset,
    isViewer,
  ])

  const avatarPortals = useMemo(() => {
    if (!showCommentAvatars || !pdfEngineRef.current) return null

    return avatars.map((avatar) => {
      const pdf = asPdfAnnotation(avatar.metadata)
      const fragment = pdf?.fragments?.[0] as PDFAnchorFragment | undefined
      if (!fragment) return null

      const layer = pdfEngineRef.current?.getBubbleLayerEl(fragment.pageIndex)
      const rect = pdfEngineRef.current?.getAnchorPageRect(fragment)
      if (!layer || !rect) return null

      return createPortal(
        <CommentAvatar
          key={avatar.commentId}
          comment={avatar.comment}
          position={{
            top: rect.y + (bubbleOffset.top ?? 0),
            left: rect.x + (bubbleOffset.left ?? 0),
          }}
          isActive={avatar.isActive}
          onClick={onAvatarClick}
        />,
        layer
      )
    })
  }, [showCommentAvatars, avatars, pdfEngineRef, bubbleOffset, onAvatarClick])

  if (isLoading && !signedUrl) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  // Show error state
  if (error && !signedUrl) {
    return (
      <div
        className="flex flex-col items-center justify-center p-8"
        role="alert"
      >
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-red-900">
            Failed to load file
          </h3>
          <p className="mb-4 text-sm text-red-700">
            {error.message || 'Please try again later.'}
          </p>
          <Button onClick={() => refetch()} variant="solid" color="primary">
            Reload
          </Button>
        </div>
      </div>
    )
  }

  // Show PDF error state
  if (pdfError) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center max-w-md">
          <h3 className="mb-2 text-lg font-semibold text-red-900">
            Failed to load PDF
          </h3>
          <p className="mb-4 text-sm text-red-700">{pdfError}</p>
          <Button onClick={() => refetch()} variant="solid" color="primary">
            Reload
          </Button>
        </div>
      </div>
    )
  }

  if (!signedUrl) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-gray-600">No PDF URL available</p>
        </div>
      </div>
    )
  }

  return (
    <ViewerSurface
      documentId={documentId}
      scrollContainerRef={containerRef}
      isLoading={isLoading && !!numPages}
      scrollClassName={cn(
        'flex flex-col relative',
        isLoading && 'items-center'
      )}
      onMouseDown={onMouseDown}
      onClick={onClickOutside}
    >
      {addCommentBubblePortal}
      {avatarPortals}

      {dialogState.documentVersionId && (
        <AddCommentDialog
          open={dialogState.open}
          onOpenChange={dialogState.onOpenChange}
          documentVersionId={dialogState.documentVersionId}
          annotation={dialogState.annotation}
          onSuccess={dialogState.onSuccess}
        />
      )}

      <div
        ref={pdfContainerRef}
        className="flex-1 flex flex-col w-fit p-4 bg-primary mx-auto relative"
      >
        <Document
          key={currentVersionId ?? signedUrl}
          file={signedUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex items-center justify-center p-8">
              <Spinner />
            </div>
          }
          error={
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <p className="text-red-600 mb-2">Failed to load PDF document</p>
                <Button
                  onClick={() => refetch()}
                  variant="solid"
                  color="primary"
                >
                  Retry
                </Button>
              </div>
            </div>
          }
        >
          {totalPages &&
            Array.from(new Array(totalPages), (_, index) => {
              const pageIndex = index
              return (
                <div
                  key={`page_${index + 1}`}
                  className="mb-4 last:mb-0 flex flex-col items-center"
                >
                  <div
                    ref={(el) => setPageRef(pageIndex, 'wrapper', el)}
                    style={{ position: 'relative' }}
                    className="shadow-lg"
                    data-page-number={index + 1}
                  >
                    <Page
                      pageNumber={index + 1}
                      onLoadSuccess={(page) => {
                        setNumPages((prev) => (prev ?? 0) + 1)
                        handlePageLoadSuccess(
                          asPdfPageProxyLike(page),
                          pageIndex
                        )
                      }}
                      loading={
                        <div className="flex items-center justify-center p-8 min-h-[800px]">
                          <Spinner />
                        </div>
                      }
                      width={800}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                    <canvas
                      ref={(el) => setPageRef(pageIndex, 'highlightCanvas', el)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none',
                        zIndex: 1,
                      }}
                    />
                    <canvas
                      ref={(el) =>
                        setPageRef(pageIndex, 'focusHighlightCanvas', el)
                      }
                      className="absolute inset-0 w-full h-full top-0 left-0 pointer-events-none z-2"
                      data-focus-highlight-canvas
                    />
                    <canvas
                      ref={(el) => setPageRef(pageIndex, 'controlCanvas', el)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        pointerEvents: 'auto',
                        zIndex: 3,
                        touchAction: 'none',
                        cursor: 'default',
                      }}
                      onPointerDown={(e) =>
                        handlePointerDown(e.nativeEvent, pageIndex)
                      }
                      onPointerMove={(e) => handlePointerMove(e.nativeEvent)}
                      onPointerUp={(e) => handlePointerUp(e.nativeEvent)}
                      onPointerCancel={() => handlePointerCancel()}
                    />
                    <div
                      ref={(el) => setPageRef(pageIndex, 'bubbleLayer', el)}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none',
                        zIndex: 4,
                      }}
                    />
                  </div>
                </div>
              )
            })}
        </Document>
      </div>
    </ViewerSurface>
  )
}
